import { useEffect, useMemo, useRef, useState } from 'react';
import {
  addMonths,
  eachDayOfInterval,
  endOfMonth,
  endOfWeek,
  format,
  isSameDay,
  isSameMonth,
  startOfMonth,
  startOfWeek,
} from 'date-fns';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import clsx from 'clsx';

interface Props {
  /** The currently selected day (day view) or the week's Monday (week view). */
  value: Date;
  /** 'day' highlights a single date; 'week' highlights the whole active-week row. */
  mode: 'day' | 'week';
  /** Sim clock — the "today" marker. */
  todayIso: string;
  /** Trigger text, e.g. "Week of 1 Jul 2026". */
  label: string;
  onSelect: (day: Date) => void;
  testId?: string;
}

const WEEKDAYS = ['Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa', 'Su'];

/**
 * A compact month-calendar popover for jumping the calendar to any date. Replaces
 * the old invisible native <input type="date">. Open/close + outside-click/Escape
 * handling mirrors the FilterDropdown in CalendarFilterBar; the month grid mirrors
 * FutureReadinessCalendar but pads to full Mon–Sun weeks so it aligns to 7 columns.
 */
export default function MiniCalendarPopover({ value, mode, todayIso, label, onSelect, testId }: Props) {
  const [open, setOpen] = useState(false);
  const [month, setMonth] = useState(() => startOfMonth(value));
  const ref = useRef<HTMLDivElement>(null);

  // Re-anchor the visible month on the current value each time the popover opens.
  useEffect(() => {
    if (open) setMonth(startOfMonth(value));
  }, [open, value]);

  useEffect(() => {
    if (!open) return;
    const outside = (e: Event) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    window.addEventListener('pointerdown', outside);
    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('pointerdown', outside);
      window.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const days = useMemo(() => {
    const gridStart = startOfWeek(startOfMonth(month), { weekStartsOn: 1 });
    const gridEnd = endOfWeek(endOfMonth(month), { weekStartsOn: 1 });
    return eachDayOfInterval({ start: gridStart, end: gridEnd });
  }, [month]);

  const weekStart = startOfWeek(value, { weekStartsOn: 1 });
  const weekEnd = endOfWeek(value, { weekStartsOn: 1 });
  const today = new Date(todayIso);

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-haspopup="true"
        aria-expanded={open}
        data-testid={testId}
        className="font-inter text-xs text-cream-soft uppercase tracking-widest hover:text-gold-bright transition"
      >
        {label}
      </button>
      {open ? (
        <div className="absolute left-1/2 -translate-x-1/2 mt-2 w-64 z-40 panel p-3 shadow-ink-soft">
          <div className="flex items-center justify-between mb-2">
            <button
              type="button"
              onClick={() => setMonth((m) => addMonths(m, -1))}
              className="text-cream-soft hover:text-gold-bright transition"
              data-testid="minical-prev"
              aria-label="Previous month"
            >
              <ChevronLeft size={16} />
            </button>
            <span className="font-inter text-sm text-cream">{format(month, 'MMMM yyyy')}</span>
            <button
              type="button"
              onClick={() => setMonth((m) => addMonths(m, 1))}
              className="text-cream-soft hover:text-gold-bright transition"
              data-testid="minical-next"
              aria-label="Next month"
            >
              <ChevronRight size={16} />
            </button>
          </div>
          <div className="grid grid-cols-7 gap-0.5 text-center">
            {WEEKDAYS.map((d) => (
              <span
                key={d}
                className="font-inter text-[9px] text-cream-soft uppercase tracking-widest py-1"
              >
                {d}
              </span>
            ))}
            {days.map((day) => {
              const inMonth = isSameMonth(day, month);
              const isToday = isSameDay(day, today);
              const active =
                mode === 'day' ? isSameDay(day, value) : day >= weekStart && day <= weekEnd;
              return (
                <button
                  key={day.toISOString()}
                  type="button"
                  onClick={() => {
                    onSelect(day);
                    setOpen(false);
                  }}
                  data-testid={`minical-day-${format(day, 'yyyy-MM-dd')}`}
                  className={clsx(
                    'h-8 rounded-sm font-mono text-xs transition flex items-center justify-center',
                    active
                      ? 'bg-gold/20 text-gold-bright'
                      : inMonth
                        ? 'text-cream hover:bg-ink-soft/60'
                        : 'text-cream-faint hover:bg-ink-soft/60',
                    isToday && !active ? 'ring-1 ring-gold-bright/70' : '',
                  )}
                >
                  {format(day, 'd')}
                </button>
              );
            })}
          </div>
        </div>
      ) : null}
    </div>
  );
}
